import { Flex, Box, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ReactComponent as GreenCircleIcon } from 'assets/green-circle.svg';
import { ReactComponent as GreenWalletIcon } from 'assets/green-wallet.svg';
import hashpackLogo from 'assets/hashpack-wallet-logo.svg';
import walletconnectLogo from 'assets/walletconnect-logo.svg';
import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../providers/Global';
import { ApplicationRoutes } from '../utils/routes';
import { HASHPACK_DOWNLOAD_URL } from '../utils/constants';
import MultisigAccounts from './components/MultisigAccounts';
import { sidebarActionCard, sidebarContainer, walletOption } from './styles/HomePage';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut', delay },
  }),
};

const underlineReveal = {
  hidden: { scaleX: 0 },
  visible: {
    scaleX: 1,
    transition: { duration: 0.6, ease: 'easeOut', delay: 0.4 },
  },
};

const HomePage = () => {
  const navigate = useNavigate();

  const {
    connectedNetwork,
    connection: {
      accountId,
      connectToHashPack,
      connectViaWalletConnect,
      isHashPackExtensionAvailable,
    },
  } = useContext(GlobalContext);

  const handleCreateClick = useCallback(() => {
    navigate(ApplicationRoutes.CreateAccount);
  }, [navigate]);

  const handleLoadClick = useCallback(() => {
    navigate(ApplicationRoutes.LoadAccount);
  }, [navigate]);

  if (!connectedNetwork || !accountId) {
    return (
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        flex={1}
        py={46}
      >
        <MotionBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          overflow="hidden"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0}
        >
          <Text
            fontSize={{ base: 32, md: 64, lg: 96 }}
            variant="bodyBold"
            align="center"
          >
            Welcome To
          </Text>
          <Text
            position="relative"
            mt={{ base: "-12px", lg: "-40px" }}
            fontSize={{ base: 32, md: 64, lg: 96 }}
            align="center"
            variant="bodyBold"
            zIndex={1}
            whiteSpace="nowrap"
          >
            Hedera MultiSig
            <MotionBox
              as='span'
              position="absolute"
              h={{ base: 3, md: 6, lg: 8 }}
              zIndex={-1}
              bg="#B7F133"
              opacity="0.9"
              bottom={{ base: '3px', md: '8px', lg: '14px' }}
              insetX={{ base: '-1px', md: '0px', lg: '2px' }}
              variants={underlineReveal}
              initial="hidden"
              animate="visible"
              style={{ originX: 0 }}
            />
          </Text>
        </MotionBox>
        <MotionBox
          mt={2}
          mb={{ base: 6, md: 10 }}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.25}
        >
          <Text
            size={{ base: '18px', md: '2xl' }}
            color="blackAlpha.500"
            fontWeight={300}
            align="center"
          >
            Shared control. Trusted security. One wallet for your team.
          </Text>
        </MotionBox>
        <MotionFlex
          gap={4}
          direction={{ base: 'column', sm: 'row' }}
          justifyContent="center"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={0.35}
        >
          <Flex
            as="button"
            onClick={isHashPackExtensionAvailable
              ? connectToHashPack
              : () => window.open(HASHPACK_DOWNLOAD_URL, '_blank', 'noopener')
            }
            sx={walletOption}
          >
            <img src={hashpackLogo} alt="HashPack" width="40" height="40" />
            <Text size="sm" fontWeight={600}>HashPack</Text>
          </Flex>
          <Flex
            as="button"
            onClick={connectViaWalletConnect}
            sx={walletOption}
          >
            <img src={walletconnectLogo} alt="WalletConnect" width="40" height="40" style={{ borderRadius: '6px' }} />
            <Text size="sm" fontWeight={600}>WalletConnect</Text>
          </Flex>
        </MotionFlex>
      </Flex>
    );
  }

  const handleCardKeyDown = (handler: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

  return (
    <Flex
      direction={{ base: 'column', md: 'row' }}
      w="100%"
      maxW={1400}
      py={6}
      gap={6}
    >
      <MotionFlex
        direction={{ base: 'row', md: 'column' }}
        gap={4}
        w={{ base: '100%', md: '280px' }}
        flexShrink={0}
        sx={sidebarContainer}
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
      >
        <MotionBox
          sx={sidebarActionCard}
          variants={fadeUp}
          custom={0}
          role="button"
          tabIndex={0}
          onClick={handleCreateClick}
          onKeyDown={handleCardKeyDown(handleCreateClick)}
        >
          <GreenCircleIcon width={36} height={36} style={{ flexShrink: 0 }} />
          <Box>
            <Text size="sm" variant="bodyBold">Create Account</Text>
            <Text
              size="xs"
              color="blackAlpha.500"
              display={{ base: 'none', md: 'block' }}
            >
              New multi-signature account
            </Text>
          </Box>
        </MotionBox>
        <MotionBox
          sx={sidebarActionCard}
          variants={fadeUp}
          custom={0}
          role="button"
          tabIndex={0}
          onClick={handleLoadClick}
          onKeyDown={handleCardKeyDown(handleLoadClick)}
        >
          <GreenWalletIcon width={36} height={36} style={{ flexShrink: 0 }} />
          <Box>
            <Text size="sm" variant="bodyBold">Load Account</Text>
            <Text
              size="xs"
              color="blackAlpha.500"
              display={{ base: 'none', md: 'block' }}
            >
              Add existing MultiSig
            </Text>
          </Box>
        </MotionBox>
      </MotionFlex>
      <MotionBox
        flex={1}
        minW={0}
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        custom={0.2}
      >
        {accountId && <MultisigAccounts />}
      </MotionBox>
    </Flex>
  );
};

export default HomePage;
